import { Schema } from 'effect';

import { DateTimeSerializableFromString } from '../DateTimeSerializableFromString';
import { overrideSchema } from '../openapi/overrideSchema';

/**
 * Branded curated content-entity schemas. The exported `*Base` structs are
 * intentionally unbranded so tenant packages can extend or replace fields
 * before applying this application's nominal field contracts.
 */

/**
 * CMS override slots whose base schemas intentionally avoid importing a
 * tenant's generated content-element closure. The tenant/app supplies the
 * exact CMS element schema via `overrideSchema`; the base intentionally stays
 * loose.
 */
export type ContentOverrideSlots = 'ArticleShow.content';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);

const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);
const ReadOnlyNullableString = Schema.optionalKey(NullableString.annotate({ readOnly: true }));
const ReadOnlyInt = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
);
const RouterUrl = Schema.String.annotate({
  description: 'Frontend router URL for this resource.',
  readOnly: true,
});

const ArticleCategoryImageBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: ReadOnlyInt,
  pathAbstract: ReadOnlyNullableString,
});

const ArticleSectionIndexBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  name: NullableString,
  slug: NullableString,
});

const ArticleSectionShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  name: NullableString,
  slug: NullableString,
});

const ArticleTagIndexBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  color: NullableString,
  id: ReadOnlyInt,
  name: NullableString,
  slug: NullableString,
});

// ─── Author ──────────────────────────────────────────────────────────────────

export const ArticleAuthorIndexBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  favourite: Schema.Boolean.annotate({ default: false }),
  name: Schema.String,
  image: NullableString,
  imageAbstract: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  metaTitle: NullableString,
  id: ReadOnlyInt,
  routerUrl: RouterUrl,
});

export const ArticleAuthorIndexFavouriteBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  name: Schema.String,
  routerUrl: RouterUrl,
});

export const ArticleAuthorShowFromArticleDetailBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  favourite: Schema.Boolean.annotate({ default: false }),
  name: Schema.String,
  image: NullableString,
  imageAbstract: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  metaTitle: NullableString,
  id: ReadOnlyInt,
  routerUrl: RouterUrl,
});

export const ArticleAuthorShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  favourite: Schema.Boolean.annotate({ default: false }),
  name: Schema.String,
  image: NullableString,
  imageAbstract: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  metaTitle: NullableString,
  metaDescription: NullableString,
  id: ReadOnlyInt,
  routerUrl: RouterUrl,
});

export const AuthorIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  favourite: Schema.Boolean.annotate({ default: false }),
  name: Schema.String,
  image: NullableString,
  imageAbstract: ReadOnlyNullableString,
  description: ReadOnlyNullableString,
  metaTitle: NullableString,
  id: ReadOnlyInt,
  routerUrl: RouterUrl,
  articlesCount: ReadOnlyInt,
});

// ─── Category / Section / Tag ───────────────────────────────────────────────

export const ArticleCategoryIndexBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  image: Schema.Union([ArticleCategoryImageBase, Schema.Null]),
  name: NullableString,
  slug: NullableString,
  routerUrl: RouterUrl,
});

export const ArticleCategoryShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  image: Schema.Union([ArticleCategoryImageBase, Schema.Null]),
  name: NullableString,
  slug: NullableString,
  description: NullableString,
  metaTitle: NullableString,
  metaDescription: NullableString,
  routerUrl: RouterUrl,
});

export const SectionIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  name: NullableString,
  slug: NullableString,
  articlesCount: ReadOnlyInt,
});

export const TagIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  color: NullableString,
  favourite: Schema.Boolean.annotate({ default: false }),
  id: ReadOnlyInt,
  name: NullableString,
  slug: NullableString,
});

export const ArticleTagShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  color: NullableString,
  favourite: Schema.Boolean.annotate({ default: false }),
  id: ReadOnlyInt,
  name: NullableString,
  slug: NullableString,
});

// ─── Article ────────────────────────────────────────────────────────────────

export const ArticleIndexBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  section: ArticleSectionIndexBase,
  categories: Schema.Array(ArticleCategoryIndexBase),
  tags: Schema.Array(ArticleTagIndexBase),
  publishedAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  authors: Schema.Array(ArticleAuthorIndexBase),
  thumbnailImageAbstract: ReadOnlyNullableString,
  id: ReadOnlyInt,
  title: NullableString,
  description: NullableString,
  slug: NullableString,
  metaTitle: NullableString,
  routerUrl: RouterUrl,
});

export const ArticleIndexFavouriteBase = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  publishedAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  authors: Schema.Array(ArticleAuthorIndexFavouriteBase),
  thumbnailImageAbstract: ReadOnlyNullableString,
  id: ReadOnlyInt,
  title: NullableString,
  description: NullableString,
  slug: NullableString,
  metaTitle: NullableString,
  routerUrl: RouterUrl,
});

export const ArticleShowBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  section: ArticleSectionShowBase,
  categories: Schema.Array(ArticleCategoryShowBase),
  image: NullableString,
  tags: Schema.Array(ArticleTagShowBase),
  publishedAt: Schema.Union([DateTimeSerializableFromString, Schema.Null]),
  authors: Schema.Array(ArticleAuthorShowFromArticleDetailBase),
  imageAbstract: ReadOnlyNullableString,
  thumbnailImageAbstract: ReadOnlyNullableString,
  metaImageAbstract: ReadOnlyNullableString,
  id: ReadOnlyInt,
  title: NullableString,
  description: NullableString,
  slug: NullableString,
  metaTitle: NullableString,
  metaDescription: NullableString,
  metaKeywords: NullableString,
  metaImage: NullableString,
  /**
   * Override slot: the tenant/app supplies the exact CMS element schema via
   * `overrideSchema`; the base intentionally stays loose.
   */
  content: Schema.Array(Schema.Unknown),
  routerUrl: RouterUrl,
  articles: Schema.optionalKey(
    Schema.Array(ArticleIndexFavouriteBase).annotate({ readOnly: true })
  ),
});

// ─── Branded fields ─────────────────────────────────────────────────────────

const ArticleIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Article.iri'))
);
export const ArticleId = Schema.Finite.pipe(Schema.brand('Article.id'));
export type ArticleId = typeof ArticleId.Type;
const ArticleIdField = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt()).pipe(Schema.brand('Article.id'))
);
const ArticleTitle = Schema.Union([Schema.String.pipe(Schema.brand('Article.title')), Schema.Null]);
const ArticleSlug = Schema.Union([Schema.String.pipe(Schema.brand('Article.slug')), Schema.Null]);
const ArticleRouterUrl = RouterUrl.pipe(Schema.brand('Article.routerUrl'));

const AuthorIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Author.iri'))
);
export const AuthorId = Schema.Finite.pipe(Schema.brand('Author.id'));
export type AuthorId = typeof AuthorId.Type;
const AuthorIdField = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt()).pipe(Schema.brand('Author.id'))
);
const AuthorName = Schema.String.pipe(Schema.brand('Author.name'));
const AuthorRouterUrl = RouterUrl.pipe(Schema.brand('Author.routerUrl'));

const CategoryIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Category.iri'))
);
export const CategoryId = Schema.String.annotate({ format: 'uuid' }).pipe(
  Schema.brand('Category.id')
);
export type CategoryId = typeof CategoryId.Type;
const CategoryCode = Schema.String.pipe(Schema.brand('Category.code'));
const CategoryName = Schema.Union([Schema.String.pipe(Schema.brand('Category.name')), Schema.Null]);
const CategorySlug = Schema.Union([Schema.String.pipe(Schema.brand('Category.slug')), Schema.Null]);
const CategoryRouterUrl = RouterUrl.pipe(Schema.brand('Category.routerUrl'));

const SectionIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Section.iri'))
);
const SectionId = Schema.String.annotate({ format: 'uuid' }).pipe(Schema.brand('Section.id'));
const SectionCode = Schema.String.pipe(Schema.brand('Section.code'));
const SectionName = Schema.Union([Schema.String.pipe(Schema.brand('Section.name')), Schema.Null]);
const SectionSlug = Schema.Union([Schema.String.pipe(Schema.brand('Section.slug')), Schema.Null]);

const TagIri = Schema.optionalKey(
  Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Tag.iri'))
);
const TagId = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt()).pipe(Schema.brand('Tag.id'))
);
const TagName = Schema.Union([Schema.String.pipe(Schema.brand('Tag.name')), Schema.Null]);
const TagSlug = Schema.Union([Schema.String.pipe(Schema.brand('Tag.slug')), Schema.Null]);

export const ArticleAuthorIndex = overrideSchema(ArticleAuthorIndexBase, {
  '@id': AuthorIri,
  id: AuthorIdField,
  name: AuthorName,
  routerUrl: AuthorRouterUrl,
}).annotate({ identifier: 'ArticleAuthorIndex' });
export type ArticleAuthorIndex = Schema.Schema.Type<typeof ArticleAuthorIndex>;

export const ArticleAuthorIndexFavourite = overrideSchema(ArticleAuthorIndexFavouriteBase, {
  '@id': AuthorIri,
  name: AuthorName,
  routerUrl: AuthorRouterUrl,
}).annotate({ identifier: 'ArticleAuthorIndexFavourite' });
export type ArticleAuthorIndexFavourite = Schema.Schema.Type<typeof ArticleAuthorIndexFavourite>;

export const ArticleAuthorShowFromArticleDetail = overrideSchema(
  ArticleAuthorShowFromArticleDetailBase,
  {
    '@id': AuthorIri,
    id: AuthorIdField,
    name: AuthorName,
    routerUrl: AuthorRouterUrl,
  }
).annotate({ identifier: 'ArticleAuthorShowFromArticleDetail' });
export type ArticleAuthorShowFromArticleDetail = Schema.Schema.Type<
  typeof ArticleAuthorShowFromArticleDetail
>;

export const ArticleAuthorShow = overrideSchema(ArticleAuthorShowBase, {
  '@id': AuthorIri,
  id: AuthorIdField,
  name: AuthorName,
  routerUrl: AuthorRouterUrl,
}).annotate({ identifier: 'ArticleAuthorShow' });
export type ArticleAuthorShow = Schema.Schema.Type<typeof ArticleAuthorShow>;

export const AuthorIndex = overrideSchema(AuthorIndexBase, {
  '@id': AuthorIri,
  id: AuthorIdField,
  name: AuthorName,
  routerUrl: AuthorRouterUrl,
}).annotate({ identifier: 'AuthorIndex' });
export type AuthorIndex = Schema.Schema.Type<typeof AuthorIndex>;

export const ArticleIndex = overrideSchema(ArticleIndexBase, {
  '@id': ArticleIri,
  id: ArticleIdField,
  title: ArticleTitle,
  slug: ArticleSlug,
  routerUrl: ArticleRouterUrl,
  authors: Schema.Array(ArticleAuthorIndex),
}).annotate({ identifier: 'ArticleIndex' });
export type ArticleIndex = Schema.Schema.Type<typeof ArticleIndex>;

export const ArticleIndexFavourite = overrideSchema(ArticleIndexFavouriteBase, {
  '@id': ArticleIri,
  id: ArticleIdField,
  title: ArticleTitle,
  slug: ArticleSlug,
  routerUrl: ArticleRouterUrl,
  authors: Schema.Array(ArticleAuthorIndexFavourite),
}).annotate({ identifier: 'ArticleIndexFavourite' });
export type ArticleIndexFavourite = Schema.Schema.Type<typeof ArticleIndexFavourite>;

export const ArticleShow = overrideSchema(ArticleShowBase, {
  '@id': ArticleIri,
  id: ArticleIdField,
  title: ArticleTitle,
  slug: ArticleSlug,
  routerUrl: ArticleRouterUrl,
  authors: Schema.Array(ArticleAuthorShowFromArticleDetail),
  articles: Schema.optionalKey(Schema.Array(ArticleIndexFavourite).annotate({ readOnly: true })),
}).annotate({ identifier: 'ArticleShow' });
export type ArticleShow = Schema.Schema.Type<typeof ArticleShow>;

export const ArticleCategoryIndex = overrideSchema(ArticleCategoryIndexBase, {
  '@id': CategoryIri,
  id: CategoryId,
  code: CategoryCode,
  name: CategoryName,
  slug: CategorySlug,
  routerUrl: CategoryRouterUrl,
}).annotate({ identifier: 'ArticleCategoryIndex' });
export type ArticleCategoryIndex = Schema.Schema.Type<typeof ArticleCategoryIndex>;

export const ArticleCategoryShow = overrideSchema(ArticleCategoryShowBase, {
  '@id': CategoryIri,
  id: CategoryId,
  code: CategoryCode,
  name: CategoryName,
  slug: CategorySlug,
  routerUrl: CategoryRouterUrl,
}).annotate({ identifier: 'ArticleCategoryShow' });
export type ArticleCategoryShow = Schema.Schema.Type<typeof ArticleCategoryShow>;

export const SectionIndex = overrideSchema(SectionIndexBase, {
  '@id': SectionIri,
  id: SectionId,
  code: SectionCode,
  name: SectionName,
  slug: SectionSlug,
}).annotate({ identifier: 'SectionIndex' });
export type SectionIndex = Schema.Schema.Type<typeof SectionIndex>;

export const TagIndex = overrideSchema(TagIndexBase, {
  '@id': TagIri,
  id: TagId,
  name: TagName,
  slug: TagSlug,
}).annotate({ identifier: 'TagIndex' });
export type TagIndex = Schema.Schema.Type<typeof TagIndex>;

export const ArticleTagShow = overrideSchema(ArticleTagShowBase, {
  '@id': TagIri,
  id: TagId,
  name: TagName,
  slug: TagSlug,
}).annotate({ identifier: 'ArticleTagShow' });
export type ArticleTagShow = Schema.Schema.Type<typeof ArticleTagShow>;
